import React, { FC } from 'react';
import styled from 'styled-components';
import {
	AttendanceDeliverySummary,
	AttendanceRosterCounts,
} from '../../../Types/AttendanceReport.types';

type CardFilterName = 'status' | 'wa';

type Props = {
	counts?: AttendanceRosterCounts;
	delivery?: AttendanceDeliverySummary;
	/** The FILTER_status / FILTER_wa values currently in the URL. */
	active?: { status?: string | null; wa?: string | null };
	/** Card click = filter shortcut; null clears both card filters. */
	onFilter?: (name: CardFilterName | null, value?: string) => void;
};

/**
 * The roster KPI cards (mirroring the desk app's roster stats) plus the
 * WhatsApp delivery cards for the current scope (group filter applied).
 * Every card is a filter shortcut: it applies its status to the table
 * (clicking again clears it); Expected clears both card filters.
 */
const AttendanceCards: FC<Props> = ({ counts, delivery, active, onFilter }) => {
	if (!counts && !delivery) return null;
	const card = (
		value: number,
		label: string,
		filter: { name: CardFilterName; value: string } | null,
		tone?: string
	) => (
		<Card
			$tone={tone}
			$active={!!filter && active?.[filter.name] === filter.value}
			title={
				filter && active?.[filter.name] === filter.value
					? 'Click to clear this filter'
					: 'Click to filter the table'
			}
			onClick={() =>
				filter ? onFilter?.(filter.name, filter.value) : onFilter?.(null)
			}
		>
			<CardValue>{value}</CardValue>
			<CardLabel>{label}</CardLabel>
		</Card>
	);
	return (
		<CardsRow>
			{counts && (
				<>
					{card(counts.expected ?? 0, 'Expected', null)}
					{card(
						counts.present_here ?? 0,
						`Present${counts.late ? ` · ${counts.late} late` : ''}`,
						{ name: 'status', value: 'present_here' },
						'#00d66b'
					)}
					{card(
						counts.missing ?? 0,
						'Missing',
						{ name: 'status', value: 'missing' },
						'#ffab00'
					)}
					{card(
						counts.no_show ?? 0,
						'No-show',
						{ name: 'status', value: 'no_show' },
						'#ff3100'
					)}
					{card(
						(counts.visitors ?? 0) + (counts.new_students ?? 0),
						'Walk-ins',
						{ name: 'status', value: 'walk_in' }
					)}
				</>
			)}
			{delivery && (
				<>
					<Divider />
					{card(delivery.unsent, 'WA unsent', {
						name: 'wa',
						value: 'unsent',
					})}
					{card(delivery.pending + delivery.sent, 'WA pending', {
						name: 'wa',
						value: 'pending',
					})}
					{card(
						delivery.delivered + delivery.read,
						'WA delivered',
						{ name: 'wa', value: 'delivered' },
						'#00d66b'
					)}
					{card(
						delivery.failed + delivery.skipped,
						'WA failed / skipped',
						{ name: 'wa', value: 'failed' },
						'#ff3100'
					)}
				</>
			)}
		</CardsRow>
	);
};

const CardsRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: stretch;
	margin-bottom: 1rem;
`;

const Card = styled.div<{ $tone?: string; $active?: boolean }>`
	min-width: 7.5rem;
	padding: 0.6rem 1rem;
	border-radius: 8px;
	background: #fff;
	border: 1px solid
		${({ $active, $tone }) => ($active ? $tone ?? '#5ac0fc' : '#e0e0e0')};
	border-inline-start: 4px solid ${({ $tone }) => $tone ?? '#5ac0fc'};
	box-shadow: ${({ $active, $tone }) =>
		$active ? `inset 0 0 0 1px ${$tone ?? '#5ac0fc'}` : 'none'};
	cursor: pointer;
	user-select: none;
	transition: box-shadow 0.15s, border-color 0.15s;
	&:hover {
		box-shadow: 0 1px 5px rgba(0, 0, 0, 0.15);
	}
`;

const CardValue = styled.div`
	font-size: 1.25rem;
	font-weight: 600;
`;

const CardLabel = styled.div`
	font-size: 0.75rem;
	color: #6c63ff;
	white-space: nowrap;
`;

const Divider = styled.div`
	width: 1px;
	background: #e0e0e0;
	margin: 0 0.25rem;
`;

export default AttendanceCards;
